import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../features/app/Store";
import useSetupUserInfo from "../features/user/useSetupUserInfo";
import useSetupTagsInfo from "../features/setup/useSetupTagsInfo";
import useSubscriptions from "../features/subscriptions/useSubscriptions";
import InitAppError from "./InitAppError";

import { fetchFeatureFlags } from "../features/featureFlags/SitesFeatureFlagsStore";
import { fetchPurchasedProducts } from "../features/purchasedProducts/PurchasedProductsStore";
import { useMixpanel } from "../common/Hooks/useMixpanel";
import { UserInitializationState } from "../features/user/Models";
import { selectApplicationInitialization } from "../features/app/store/InitializationSelectors";
import { SiteGroupsContext, useLoadSiteGroups } from "../features/site_groups/useSiteGroups";

interface IProps {
  loadingIcon: ReactNode;
  children: ReactNode;
}
const InitApp: FC<IProps> = ({ loadingIcon, children }) => {
  useSetupUserInfo();
  useMixpanel();
  const dispatch = useDispatch<AppDispatch>();
  const userData = useSelector((state: RootState) => state.user);
  const siteGroups = useLoadSiteGroups(
    userData.initializationState === UserInitializationState.Initialized
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const siteIds =
    userData.initializationState === UserInitializationState.Initialized ? userData.siteIds : [];
  const siteIdsKey = siteIds.join(",");
  const initializedSiteIds = useMemo(() => siteIds, [siteIdsKey]);

  useEffect(() => {
    if (initializedSiteIds.length === 0) {
      return;
    }
    const flagsRequest = dispatch(fetchFeatureFlags(initializedSiteIds));
    const productsRequest = dispatch(fetchPurchasedProducts(initializedSiteIds));
    return () => {
      flagsRequest.abort();
      productsRequest.abort();
    };
  }, [dispatch, initializedSiteIds, refreshKey]);

  useSubscriptions(initializedSiteIds);

  useSetupTagsInfo({
    siteIds: initializedSiteIds,
    refreshKey,
  });
  const initialization = useSelector(selectApplicationInitialization);
  if (initialization.status === "error") {
    return <InitAppError error={initialization.error} onRetry={() => setRefreshKey((key) => key + 1)} />;
  }
  return (
    <SiteGroupsContext.Provider value={siteGroups}>
      {initialization.status === "ready" && !siteGroups.loading ? children : loadingIcon}
    </SiteGroupsContext.Provider>
  );
};
export default InitApp;
